import { dbError } from "@/lib/db-error";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertStaff } from "./admin.server";
import { ADDON_SELECT, fetchAddon, fetchAddons, fetchPricing, mapAddon } from "./pricing.server";
import type { AddonDetail, PricingAddon, PricingData, PricingRegion, RegionalPrice } from "./pricing.types";
import { LOCALES } from "./i18n";

const localeInput = z.object({ locale: z.enum(LOCALES).default("en") });

/** Public read used by the Pricing route loader (SSR-safe, no auth). */
export const getPricing = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => localeInput.parse(data ?? {}))
  .handler(async ({ data }): Promise<PricingData> => fetchPricing(data.locale));

/** Public read for the /addons hub. */
export const getAddons = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => localeInput.parse(data ?? {}))
  .handler(async ({ data }): Promise<PricingAddon[]> => fetchAddons(data.locale));

/** Public read for a single add-on page. */
export const getAddon = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ slug: z.string().trim().min(1).max(120), locale: z.enum(LOCALES).default("en") }).parse(data))
  .handler(async ({ data }): Promise<AddonDetail | null> => fetchAddon(data.slug, data.locale));

const uuid = z.string().uuid();
const text = (max = 400) => z.string().trim().max(max);

const planSchema = z.object({
  id: uuid.optional(),
  slug: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(120),
  price: text(80).default(""),
  period: text(80).default(""),
  blurb: text(400).default(""),
  features: z.array(z.string().trim().max(240)).max(40).default([]),
  cta: text(80).default("Get started"),
  highlighted: z.boolean().default(false),
  published: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(9999).default(0),
});

const tierSchema = z.object({
  id: uuid.optional(),
  name: z.string().trim().min(1).max(120),
  price: text(80).default(""),
  period: text(80).default(""),
  blurb: text(400).default(""),
  features: z.array(z.string().trim().max(240)).max(40).default([]),
  sort_order: z.number().int().min(0).max(9999).default(0),
});

const faqSchema = z.object({
  question: z.string().trim().min(1).max(300),
  answer: z.string().trim().max(4000).default(""),
  sort_order: z.number().int().min(0).max(9999).default(0),
});

const stepSchema = z.object({
  title: z.string().trim().min(1).max(200),
  body: z.string().trim().max(2000).default(""),
  iso: z.string().trim().max(60).default(""),
  image_url: z.string().trim().max(600).default(""),
  sort_order: z.number().int().min(0).max(9999).default(0),
});

const addonSchema = z.object({
  id: uuid.optional(),
  slug: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(120),
  tagline: text(240).default(""),
  description: text(600).default(""),
  body: z.string().trim().max(20000).default(""),
  highlights: z.array(z.string().trim().max(240)).max(40).default([]),
  icon: text(60).default(""),
  featured: z.boolean().default(false),
  price: text(80).default(""),
  period: text(80).default(""),
  published: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(9999).default(0),
  planIds: z.array(uuid).max(50).default([]),
  tiers: z.array(tierSchema).max(20).default([]),
  faqs: z.array(faqSchema).max(30).default([]),
  howLead: z.string().trim().max(600).default(""),
  steps: z.array(stepSchema).max(12).default([]),
});

/** Admin read: includes unpublished plans and add-ons. */
export const adminGetPricing = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PricingData> => {
    await assertStaff(context.supabase);
    const sb = context.supabase;

    const [plansRes, groupsRes, valuesRes, addonsRes] = await Promise.all([
      sb
        .from("pricing_plans")
        .select("id, slug, name, price, period, blurb, features, cta, highlighted, published, sort_order")
        .order("sort_order", { ascending: true }),
      sb
        .from("pricing_feature_groups")
        .select("id, title, sort_order, pricing_features(id, label, sort_order)")
        .order("sort_order", { ascending: true }),
      sb.from("pricing_feature_values").select("feature_id, plan_id, value"),
      sb.from("pricing_addons").select(ADDON_SELECT).order("sort_order", { ascending: true }),
    ]);

    const error = plansRes.error ?? groupsRes.error ?? valuesRes.error ?? addonsRes.error;
    if (error) throw dbError(error);

    const valueMap = new Map<string, Record<string, string>>();
    for (const v of valuesRes.data ?? []) {
      const bucket = valueMap.get(v.feature_id) ?? {};
      bucket[v.plan_id] = v.value;
      valueMap.set(v.feature_id, bucket);
    }

    const [regionsRes, planPricesRes, addonPricesRes, tierPricesRes] = await Promise.all([
      sb.from("pricing_regions").select("id, code, name, locale, currency, active, public_visible, sort_order").order("sort_order"),
      sb.from("pricing_plan_region_prices").select("id, plan_id, region_id, amount, period"),
      sb.from("pricing_addon_region_prices").select("id, addon_id, region_id, amount, period"),
      sb.from("pricing_addon_tier_region_prices").select("id, tier_id, region_id, amount, period"),
    ]);
    const regionalError = regionsRes.error ?? planPricesRes.error ?? addonPricesRes.error ?? tierPricesRes.error;
    if (regionalError) throw dbError(regionalError);
    const groupPrices = (rows: Array<RegionalPrice & { parentId: string }>) => {
      const grouped: Record<string, RegionalPrice[]> = {};
      for (const { parentId, ...price } of rows) (grouped[parentId] ??= []).push(price);
      return grouped;
    };
    return {
      plans: (plansRes.data ?? []).map((p) => ({
        ...p,
        features: Array.isArray(p.features) ? p.features.map((f) => String(f)) : [],
      })),
      groups: (
        (groupsRes.data ?? []) as unknown as {
          id: string;
          title: string;
          sort_order: number;
          pricing_features: { id: string; label: string; sort_order: number }[];
        }[]
      ).map((g) => ({
        id: g.id,
        title: g.title,
        sort_order: g.sort_order,
        features: [...(g.pricing_features ?? [])]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((f) => ({ ...f, values: valueMap.get(f.id) ?? {} })),
      })),
      addons: ((addonsRes.data ?? []) as unknown as Parameters<typeof mapAddon>[0][]).map(mapAddon),
      regions: (regionsRes.data ?? []) as PricingRegion[],
      planRegionPrices: groupPrices((planPricesRes.data ?? []).map((r) => ({ ...r, amount: Number(r.amount), parentId: r.plan_id }))),
      addonRegionPrices: groupPrices((addonPricesRes.data ?? []).map((r) => ({ ...r, amount: Number(r.amount), parentId: r.addon_id }))),
      tierRegionPrices: groupPrices((tierPricesRes.data ?? []).map((r) => ({ ...r, amount: Number(r.amount), parentId: r.tier_id }))),
    };
  });

const workbookCell = z.union([z.string(), z.number(), z.boolean()]);
const workbookRow = z.record(z.string(), workbookCell);
const workbookSchema = z.object({
  sheets: z.record(z.string(), z.array(workbookRow).max(5000)).refine((value) => Object.keys(value).length <= 12, "Too many sheets"),
});

const cell = (row: Record<string, string | number | boolean>, key: string) => String(row[key] ?? "").trim();
const booleanCell = (row: Record<string, string | number | boolean>, key: string) => {
  const value = cell(row, key).toLowerCase();
  if (!value) return undefined;
  if (["true", "yes", "1"].includes(value)) return true;
  if (["false", "no", "0"].includes(value)) return false;
  throw new Error(`${key} must be true or false`);
};
const numberCell = (row: Record<string, string | number | boolean>, key: string, fallback = 0) => {
  const value = cell(row, key);
  if (!value) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) throw new Error(`${key} must be a number`);
  return parsed;
};
const splitItems = (value: string) => value ? value.split("|").map((item) => item.trim()).filter(Boolean) : [];

/** Protected bulk updater for workbooks generated by the Pricing admin. Omitted rows are never deleted. */
export const adminImportPricingWorkbook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => workbookSchema.parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const sb = context.supabase;
    const sheet = (name: string) => data.sheets[name] ?? [];
    let updated = 0;

    const [plansRes, groupsRes, featuresRes, addonsRes, tiersRes, regionsRes] = await Promise.all([
      sb.from("pricing_plans").select("id, slug, name"),
      sb.from("pricing_feature_groups").select("id, title"),
      sb.from("pricing_features").select("id, group_id, label"),
      sb.from("pricing_addons").select("id, slug, name"),
      sb.from("pricing_addon_tiers").select("id, addon_id, name"),
      sb.from("pricing_regions").select("id, code, name"),
    ]);
    const firstError = plansRes.error ?? groupsRes.error ?? featuresRes.error ?? addonsRes.error ?? tiersRes.error ?? regionsRes.error;
    if (firstError) throw dbError(firstError);

    const plans = [...(plansRes.data ?? [])];
    const groups = [...(groupsRes.data ?? [])];
    const features = [...(featuresRes.data ?? [])];
    const addons = [...(addonsRes.data ?? [])];
    const tiers = [...(tiersRes.data ?? [])];
    const regions = regionsRes.data ?? [];
    const fail = (name: string, index: number, message: string): never => { throw new Error(`${name}, row ${index + 2}: ${message}`); };

    for (const [index, row] of sheet("Plans").entries()) {
      const id = cell(row, "id"); const slug = cell(row, "slug"); const name = cell(row, "name");
      if (!slug || !name) fail("Plans", index, "slug and name are required");
      const existing = id ? plans.find((item) => item.id === id) : plans.find((item) => item.slug === slug);
      if (id && !existing) fail("Plans", index, "unknown plan id");
      const payload = {
        slug, name, price: cell(row, "base_price"), period: cell(row, "period"), blurb: cell(row, "blurb"),
        features: splitItems(cell(row, "included_items")), cta: cell(row, "cta") || "Get started",
        highlighted: booleanCell(row, "highlighted") ?? false, published: booleanCell(row, "published") ?? true,
        sort_order: numberCell(row, "sort_order"),
      };
      const result = existing
        ? await sb.from("pricing_plans").update(payload).eq("id", existing.id).select("id, slug, name").single()
        : await sb.from("pricing_plans").insert(payload).select("id, slug, name").single();
      if (result.error) fail("Plans", index, result.error.message);
      if (!existing && result.data) plans.push(result.data); updated++;
    }

    for (const [index, row] of sheet("Features").entries()) {
      const groupId = cell(row, "group_id"); const groupTitle = cell(row, "group");
      let group = groupId ? groups.find((item) => item.id === groupId) : groups.find((item) => item.title === groupTitle);
      if (groupId && !group) fail("Features", index, "unknown group id");
      if (!group) {
        if (!groupTitle) fail("Features", index, "group is required");
        const created = await sb.from("pricing_feature_groups").insert({ title: groupTitle, sort_order: numberCell(row, "group_sort_order") }).select("id, title").single();
        if (created.error) fail("Features", index, created.error.message);
        if (!created.data) throw new Error(`Features, row ${index + 2}: group could not be created`);
        group = created.data;
        groups.push(created.data);
        updated++;
      }
      if (!group) throw new Error(`Features, row ${index + 2}: group could not be resolved`);
      const resolvedGroup = group;
      const id = cell(row, "id"); const label = cell(row, "label");
      if (!label) fail("Features", index, "label is required");
      const existing = id ? features.find((item) => item.id === id) : features.find((item) => item.group_id === resolvedGroup.id && item.label === label);
      if (id && !existing) fail("Features", index, "unknown feature id");
      const payload = { group_id: resolvedGroup.id, label, sort_order: numberCell(row, "sort_order") };
      const result = existing
        ? await sb.from("pricing_features").update(payload).eq("id", existing.id).select("id, group_id, label").single()
        : await sb.from("pricing_features").insert(payload).select("id, group_id, label").single();
      if (result.error) fail("Features", index, result.error.message);
      if (!existing && result.data) features.push(result.data); updated++;
    }

    for (const [index, row] of sheet("Feature values").entries()) {
      const feature = cell(row, "feature_id") ? features.find((item) => item.id === cell(row, "feature_id")) : features.find((item) => item.label === cell(row, "feature"));
      const plan = cell(row, "plan_id") ? plans.find((item) => item.id === cell(row, "plan_id")) : plans.find((item) => item.name === cell(row, "plan"));
      if (!feature) throw new Error(`Feature values, row ${index + 2}: unknown feature`);
      if (!plan) throw new Error(`Feature values, row ${index + 2}: unknown plan`);
      const value = cell(row, "value"); if (!value) continue;
      const result = await sb.from("pricing_feature_values").upsert({ feature_id: feature.id, plan_id: plan.id, value }, { onConflict: "feature_id,plan_id" });
      if (result.error) fail("Feature values", index, result.error.message); updated++;
    }

    for (const [index, row] of sheet("Add-ons").entries()) {
      const id = cell(row, "id"); const slug = cell(row, "slug"); const name = cell(row, "name");
      if (!slug || !name) fail("Add-ons", index, "slug and name are required");
      const existing = id ? addons.find((item) => item.id === id) : addons.find((item) => item.slug === slug);
      if (id && !existing) fail("Add-ons", index, "unknown add-on id");
      const payload = { slug, name, price: cell(row, "base_price"), period: cell(row, "period"), tagline: cell(row, "tagline"), description: cell(row, "description"), published: booleanCell(row, "published") ?? true, featured: booleanCell(row, "featured") ?? false, sort_order: numberCell(row, "sort_order") };
      const result = existing
        ? await sb.from("pricing_addons").update(payload).eq("id", existing.id).select("id, slug, name").single()
        : await sb.from("pricing_addons").insert({ ...payload, body: "", highlights: [], icon: "", how_lead: "" }).select("id, slug, name").single();
      if (result.error) fail("Add-ons", index, result.error.message);
      if (!existing && result.data) addons.push(result.data); updated++;
    }

    for (const [index, row] of sheet("Add-on plans").entries()) {
      const addon = cell(row, "addon_id") ? addons.find((item) => item.id === cell(row, "addon_id")) : addons.find((item) => item.name === cell(row, "addon"));
      const plan = cell(row, "plan_id") ? plans.find((item) => item.id === cell(row, "plan_id")) : plans.find((item) => item.name === cell(row, "plan"));
      if (!addon) throw new Error(`Add-on plans, row ${index + 2}: unknown add-on`);
      if (!plan) throw new Error(`Add-on plans, row ${index + 2}: unknown plan`);
      const available = booleanCell(row, "available"); if (available === undefined) continue;
      const result = available
        ? await sb.from("pricing_addon_plans").upsert({ addon_id: addon.id, plan_id: plan.id }, { onConflict: "addon_id,plan_id" })
        : await sb.from("pricing_addon_plans").delete().eq("addon_id", addon.id).eq("plan_id", plan.id);
      if (result.error) fail("Add-on plans", index, result.error.message); updated++;
    }

    for (const [index, row] of sheet("Tiers").entries()) {
      const addon = cell(row, "addon_id") ? addons.find((item) => item.id === cell(row, "addon_id")) : addons.find((item) => item.name === cell(row, "addon"));
      if (!addon) throw new Error(`Tiers, row ${index + 2}: unknown add-on`);
      const id = cell(row, "id"); const name = cell(row, "name"); if (!name) fail("Tiers", index, "name is required");
      const existing = id ? tiers.find((item) => item.id === id) : tiers.find((item) => item.addon_id === addon.id && item.name === name);
      if (id && !existing) fail("Tiers", index, "unknown tier id");
      const payload = { addon_id: addon.id, name, price: cell(row, "base_price"), period: cell(row, "period"), blurb: cell(row, "blurb"), features: splitItems(cell(row, "included_items")), sort_order: numberCell(row, "sort_order") };
      const result = existing
        ? await sb.from("pricing_addon_tiers").update(payload).eq("id", existing.id).select("id, addon_id, name").single()
        : await sb.from("pricing_addon_tiers").insert(payload).select("id, addon_id, name").single();
      if (result.error) fail("Tiers", index, result.error.message);
      if (!existing && result.data) tiers.push(result.data); updated++;
    }

    for (const [index, row] of sheet("Regional prices").entries()) {
      const amountText = cell(row, "amount"); if (!amountText) continue;
      const amount = numberCell(row, "amount"); if (amount < 0) fail("Regional prices", index, "amount cannot be negative");
      const region = cell(row, "region_id") ? regions.find((item) => item.id === cell(row, "region_id")) : regions.find((item) => item.code === cell(row, "region"));
      if (!region) throw new Error(`Regional prices, row ${index + 2}: unknown region`);
      const type = cell(row, "entity_type"); const entityId = cell(row, "entity_id"); const entityName = cell(row, "entity");
      const common = { region_id: region.id, amount, period: cell(row, "period") };
      let result: { error: { message: string } | null };
      if (type === "plan") { const entity = entityId ? plans.find((item) => item.id === entityId) : plans.find((item) => item.name === entityName); if (!entity) throw new Error(`Regional prices, row ${index + 2}: unknown plan`); result = await sb.from("pricing_plan_region_prices").upsert({ ...common, plan_id: entity.id }, { onConflict: "plan_id,region_id" }); }
      else if (type === "addon") { const entity = entityId ? addons.find((item) => item.id === entityId) : addons.find((item) => item.name === entityName); if (!entity) throw new Error(`Regional prices, row ${index + 2}: unknown add-on`); result = await sb.from("pricing_addon_region_prices").upsert({ ...common, addon_id: entity.id }, { onConflict: "addon_id,region_id" }); }
      else if (type === "tier") { const entity = tiers.find((item) => item.id === entityId); if (!entity) throw new Error(`Regional prices, row ${index + 2}: unknown tier`); result = await sb.from("pricing_addon_tier_region_prices").upsert({ ...common, tier_id: entity.id }, { onConflict: "tier_id,region_id" }); }
      else throw new Error(`Regional prices, row ${index + 2}: entity_type must be plan, addon or tier`);
      if (result.error) fail("Regional prices", index, result.error.message); updated++;
    }
    return { updated };
  });

export const adminSaveRegionalPrice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({
    entity: z.enum(["plan", "addon", "tier"]),
    entityId: uuid,
    regionId: uuid,
    amount: z.number().min(0).max(9999999999),
    period: text(80).default(""),
  }).parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const common = { region_id: data.regionId, amount: data.amount, period: data.period };
    const result = data.entity === "plan"
      ? await context.supabase.from("pricing_plan_region_prices").upsert(
          { ...common, plan_id: data.entityId }, { onConflict: "plan_id,region_id" },
        )
      : data.entity === "addon"
        ? await context.supabase.from("pricing_addon_region_prices").upsert(
            { ...common, addon_id: data.entityId }, { onConflict: "addon_id,region_id" },
          )
        : await context.supabase.from("pricing_addon_tier_region_prices").upsert(
            { ...common, tier_id: data.entityId }, { onConflict: "tier_id,region_id" },
          );
    const { error } = result;
    if (error) throw dbError(error);
    return { ok: true as const };
  });

export const adminSavePricingRegion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({
    id: uuid,
    name: z.string().trim().min(1).max(120),
    locale: z.enum(LOCALES),
    active: z.boolean(),
    publicVisible: z.boolean().default(true),
    sort_order: z.number().int().min(0).max(9999),
  }).parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const { error } = await context.supabase.from("pricing_regions").update({
      name: data.name,
      locale: data.locale,
      active: data.active,
      public_visible: data.publicVisible,
      sort_order: data.sort_order,
    }).eq("id", data.id);
    if (error) throw dbError(error);
    return { ok: true as const };
  });

export const adminSavePlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => planSchema.parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const payload = {
      slug: data.slug,
      name: data.name,
      price: data.price,
      period: data.period,
      blurb: data.blurb,
      features: data.features,
      cta: data.cta,
      highlighted: data.highlighted,
      published: data.published,
      sort_order: data.sort_order,
    };
    const query = data.id
      ? context.supabase.from("pricing_plans").update(payload).eq("id", data.id).select("id").maybeSingle()
      : context.supabase.from("pricing_plans").insert(payload).select("id").maybeSingle();
    const { data: row, error } = await query;
    if (error) throw dbError(error);
    return { id: row?.id as string };
  });

export const adminDeletePlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: uuid }).parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const { error } = await context.supabase.from("pricing_plans").delete().eq("id", data.id);
    if (error) throw dbError(error);
    return { ok: true as const };
  });

export const adminSaveFeatureGroup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: uuid.optional(),
        title: z.string().trim().min(1).max(120),
        sort_order: z.number().int().min(0).max(9999).default(0),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const payload = { title: data.title, sort_order: data.sort_order };
    const { error } = data.id
      ? await context.supabase.from("pricing_feature_groups").update(payload).eq("id", data.id)
      : await context.supabase.from("pricing_feature_groups").insert(payload);
    if (error) throw dbError(error);
    return { ok: true as const };
  });

export const adminDeleteFeatureGroup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: uuid }).parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const { error } = await context.supabase.from("pricing_feature_groups").delete().eq("id", data.id);
    if (error) throw dbError(error);
    return { ok: true as const };
  });

export const adminSaveFeature = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: uuid.optional(),
        group_id: uuid,
        label: z.string().trim().min(1).max(240),
        sort_order: z.number().int().min(0).max(9999).default(0),
        values: z.record(uuid, z.string().trim().max(80)).default({}),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const payload = { group_id: data.group_id, label: data.label, sort_order: data.sort_order };

    let featureId = data.id;
    if (featureId) {
      const { error } = await context.supabase.from("pricing_features").update(payload).eq("id", featureId);
      if (error) throw dbError(error);
    } else {
      const { data: row, error } = await context.supabase
        .from("pricing_features")
        .insert(payload)
        .select("id")
        .maybeSingle();
      if (error) throw dbError(error);
      featureId = row?.id as string;
    }

    const rows = Object.entries(data.values).map(([plan_id, value]) => ({
      feature_id: featureId as string,
      plan_id,
      value: value || "no",
    }));
    if (rows.length) {
      const { error } = await context.supabase
        .from("pricing_feature_values")
        .upsert(rows, { onConflict: "feature_id,plan_id" });
      if (error) throw dbError(error);
    }
    return { id: featureId as string };
  });

export const adminDeleteFeature = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: uuid }).parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const { error } = await context.supabase.from("pricing_features").delete().eq("id", data.id);
    if (error) throw dbError(error);
    return { ok: true as const };
  });

export const adminSaveAddon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => addonSchema.parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const payload = {
      slug: data.slug,
      name: data.name,
      tagline: data.tagline,
      description: data.description,
      body: data.body,
      highlights: data.highlights,
      icon: data.icon,
      featured: data.featured,
      price: data.price,
      period: data.period,
      published: data.published,
      sort_order: data.sort_order,
      how_lead: data.howLead,
    };

    let addonId = data.id;
    if (addonId) {
      const { error } = await context.supabase.from("pricing_addons").update(payload).eq("id", addonId);
      if (error) throw dbError(error);
    } else {
      const { data: row, error } = await context.supabase
        .from("pricing_addons")
        .insert(payload)
        .select("id")
        .maybeSingle();
      if (error) throw dbError(error);
      addonId = row?.id as string;
    }

    const { error: delError } = await context.supabase
      .from("pricing_addon_plans")
      .delete()
      .eq("addon_id", addonId as string);
    if (delError) throw dbError(delError);

    if (data.planIds.length) {
      const { error } = await context.supabase
        .from("pricing_addon_plans")
        .insert(data.planIds.map((plan_id) => ({ addon_id: addonId as string, plan_id })));
      if (error) throw dbError(error);
    }

    // Preserve existing tier ids so their regional prices remain attached.
    const keptTierIds: string[] = [];
    for (const tier of data.tiers) {
      const { id, ...tierPayload } = tier;
      if (id) {
        const { error } = await context.supabase.from("pricing_addon_tiers").update(tierPayload).eq("id", id).eq("addon_id", addonId as string);
        if (error) throw dbError(error);
        keptTierIds.push(id);
      } else {
        const { data: row, error } = await context.supabase.from("pricing_addon_tiers")
          .insert({ ...tierPayload, addon_id: addonId as string }).select("id").single();
        if (error) throw dbError(error);
        keptTierIds.push(row.id);
      }
    }
    let staleTiers = context.supabase.from("pricing_addon_tiers").delete().eq("addon_id", addonId as string);
    if (keptTierIds.length) staleTiers = staleTiers.not("id", "in", `(${keptTierIds.join(",")})`);
    const { error: tierDelError } = await staleTiers;
    if (tierDelError) throw dbError(tierDelError);
    // FAQs are replaced on save, same as tiers.
    const { error: faqDelError } = await context.supabase
      .from("pricing_addon_faqs")
      .delete()
      .eq("addon_id", addonId as string);
    if (faqDelError) throw dbError(faqDelError);

    if (data.faqs.length) {
      const { error } = await context.supabase
        .from("pricing_addon_faqs")
        .insert(data.faqs.map((f) => ({ ...f, addon_id: addonId as string })));
      if (error) throw dbError(error);
    }
    // "How it works" steps are replaced on save, same as FAQs.
    const { error: stepDelError } = await context.supabase
      .from("pricing_addon_steps")
      .delete()
      .eq("addon_id", addonId as string);
    if (stepDelError) throw dbError(stepDelError);

    if (data.steps.length) {
      const { error } = await context.supabase
        .from("pricing_addon_steps")
        .insert(data.steps.map((step) => ({ ...step, addon_id: addonId as string })));
      if (error) throw dbError(error);
    }
    return { id: addonId as string };
  });

export const adminDeleteAddon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: uuid }).parse(data))
  .handler(async ({ context, data }) => {
    await assertStaff(context.supabase);
    const { error } = await context.supabase.from("pricing_addons").delete().eq("id", data.id);
    if (error) throw dbError(error);
    return { ok: true as const };
  });
